import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbChevronRight, TbRefresh } from "react-icons/tb";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import {
  isBaasHmlInstance,
  updateBaasInstance,
} from "../../../../../Services/baasInstancesApi";
import { BAAS_HOMOLOGACAO_PATH } from "../../constants";
import {
  areRequiredHomologStepsDone,
  getHomologStepSource,
  getHomologStepsForCapabilities,
  getRequiredHomologIds,
} from "../baasHomologacao";
import { useHomologChecklist } from "../useHomologChecklist";

/**
 * Compact HML gate on Settings — full wizard lives at /baas/homologacao.
 * `mode="client"`: step checklist for instance admin.
 * `mode="valut"`: Ativar / Desativar (active flag only — not is_production).
 * HML twins are born Active=true; Desativar suspends the twin; Ativar re-enables.
 */
export const BaasHmlChecklistPanel = ({
  instance,
  onActivated,
  onDeactivated,
  mode = "client",
}) => {
  const { capabilities } = useBaasCapabilities();
  const instanceKey = instance?.id || instance?.baasInstanceId;
  const env = String(instance?.environment ?? "").toLowerCase();
  // Never render the HML checklist on a production-lane card.
  const isHml =
    env !== "production" &&
    env !== "prod" &&
    isBaasHmlInstance(instance);
  const isPending = isHml && instance?.active === false;
  const isActive = isHml && instance?.active !== false;
  const showSteps = mode !== "valut";

  const visibleSteps = useMemo(
    () => getHomologStepsForCapabilities(capabilities),
    [capabilities],
  );
  const requiredIds = useMemo(
    () => getRequiredHomologIds(visibleSteps),
    [visibleSteps],
  );

  const { checks, sources, detecting, toggle, refresh } = useHomologChecklist({
    checklistKey: instanceKey,
    instance,
    visibleSteps,
    enabled: Boolean(isHml && instanceKey),
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const requiredDone = useMemo(
    () => areRequiredHomologStepsDone(checks, visibleSteps),
    [checks, visibleSteps],
  );

  if (!isHml || !instanceKey) return null;

  const handleActivate = async () => {
    if (!requiredDone || busy) return;
    setBusy(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance,
        active: true,
      });
      onActivated?.();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.instances.hmlChecklist.approveError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (busy) return;
    const confirmed = window.confirm(
      intl.get("baas.instances.hmlChecklist.deactivateConfirm"),
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance,
        active: false,
      });
      onDeactivated?.();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.instances.hmlChecklist.deactivateError"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.instances.hmlChecklist.title")}
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {intl.get(
              showSteps
                ? "baas.instances.hmlChecklist.subtitle"
                : "baas.homologacao.valutHmlGateHint",
            )}
          </p>
        </div>
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
            isPending
              ? "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100"
              : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
          }`}
        >
          {isPending
            ? intl.get("baas.instances.hmlChecklist.pendingBadge")
            : intl.get("baas.instances.hmlChecklist.activeBadge")}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Link
          to={BAAS_HOMOLOGACAO_PATH}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#a66f1d] hover:underline"
        >
          {intl.get(
            showSteps
              ? "baas.homologacao.openWizard"
              : "baas.homologacao.valutInspectHml",
          )}
          <TbChevronRight size={14} />
        </Link>
        {showSteps ? (
          <button
            type="button"
            onClick={() => refresh()}
            disabled={detecting}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 transition enabled:hover:text-[#a66f1d] disabled:opacity-50 dark:text-gray-400"
          >
            <TbRefresh
              size={12}
              className={detecting ? "animate-spin" : undefined}
            />
            {detecting
              ? intl.get("baas.homologacao.detecting")
              : intl.get("baas.homologacao.refreshDetect")}
          </button>
        ) : null}
      </div>

      {showSteps ? (
        <>
          <ul className="mt-3 space-y-2">
            {visibleSteps.map((step) => {
              const done = Boolean(checks[step.id]);
              const source = getHomologStepSource(sources, step.id);
              return (
                <li key={step.id}>
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-brand-dark dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={done}
                      onChange={() => toggle(step.id)}
                    />
                    <span>
                      {intl.get(`baas.homologacao.steps.${step.id}`)}
                      {!requiredIds.includes(step.id) ? (
                        <span className="ml-1 text-[10px] text-gray-400">
                          ({intl.get("baas.homologacao.optional")})
                        </span>
                      ) : null}
                      {done && source === "auto" ? (
                        <span className="ml-1 text-[10px] text-sky-700 dark:text-sky-300">
                          · {intl.get("baas.homologacao.sourceAuto")}
                        </span>
                      ) : null}
                      {done && source === "manual" ? (
                        <span className="ml-1 text-[10px] text-violet-700 dark:text-violet-300">
                          · {intl.get("baas.homologacao.sourceManual")}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>

          <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
            {intl.get("baas.instances.hmlChecklist.saveLocal")}
          </p>
        </>
      ) : (
        <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.homologacao.progressLabel", {
            done: visibleSteps.filter((s) => Boolean(checks[s.id])).length,
            total: visibleSteps.length,
          })}
        </p>
      )}

      {mode === "valut" && isPending ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={!requiredDone || busy}
            onClick={handleActivate}
            className="inline-flex h-9 items-center justify-center rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition enabled:hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy
              ? intl.get("baas.instances.hmlChecklist.approving")
              : intl.get("baas.instances.hmlChecklist.approve")}
          </button>
          {!requiredDone ? (
            <p className="text-[11px] text-amber-800 dark:text-amber-200">
              {intl.get("baas.homologacao.requiredHint")}
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === "valut" && isActive ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={busy}
            onClick={handleDeactivate}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-amber-300 bg-white px-4 text-xs font-semibold text-amber-900 transition enabled:hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-transparent dark:text-amber-100 dark:enabled:hover:bg-amber-950/40"
          >
            {busy
              ? intl.get("baas.instances.hmlChecklist.deactivating")
              : intl.get("baas.instances.hmlChecklist.deactivate")}
          </button>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.hmlChecklist.deactivateHint")}
          </p>
        </div>
      ) : null}

      {isPending && showSteps ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {intl.get("baas.homologacao.partnerReadonlyGates")}
          </p>
        </div>
      ) : null}

      {error ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};
